import { Dictionary } from 'lodash';
import { useMemo } from 'react';
import { Field } from 'api/tasks';
import { ProjectFinancialsSettingsContextProps } from 'contexts/project-financials-settings';
import { getFinancialsFieldIdFromLookupByName } from 'utils/project-financials';
import { HasAccessProps, useHasAccess } from './use-has-access';

interface Props {
  projectId: number;
  financialFieldsLookupByName: Dictionary<Field> | undefined;
}
export const useProjectFinancialsSettings = ({
  projectId,
  financialFieldsLookupByName,
}: Props): ProjectFinancialsSettingsContextProps => {
  // const projectSettingsFieldNames: ProjectFinancialsSettingsFields[] = [
  //   'projectBudget',
  //   'projectHours',
  //   'projectCustomBillRate',
  //   'projectRateCardUpdate',
  // ];

  const projectBudgetFieldId = useMemo(
    () =>
      getFinancialsFieldIdFromLookupByName(
        'projectBudget',
        financialFieldsLookupByName,
      ),
    [financialFieldsLookupByName],
  );

  const projectHoursFieldId = useMemo(
    () =>
      getFinancialsFieldIdFromLookupByName(
        'projectHours',
        financialFieldsLookupByName,
      ),
    [financialFieldsLookupByName],
  );

  const projectRateCardUpdateFieldId = useMemo(
    () =>
      getFinancialsFieldIdFromLookupByName(
        'projectRateCardUpdate',
        financialFieldsLookupByName,
      ),
    [financialFieldsLookupByName],
  );

  const projectCustomBillRateFieldId = useMemo(
    () =>
      getFinancialsFieldIdFromLookupByName(
        'projectCustomBillRate',
        financialFieldsLookupByName,
      ),
    [financialFieldsLookupByName],
  );

  const projectProfitabilityFieldId = useMemo(
    () =>
      getFinancialsFieldIdFromLookupByName(
        'projectProfitability',
        financialFieldsLookupByName,
      ),
    [financialFieldsLookupByName],
  );

  const commonPermissionProps: HasAccessProps = useMemo(
    () => ({
      resource: 'projects',
      resourceId: projectId,
      resourceModule: 'fields',
      newProjectPermissionsApi: true,
    }),
    [projectId],
  );

  const { hasAccess: canReadBudget } = useHasAccess({
    ...commonPermissionProps,
    permissions: ['READ'],
    resourceModuleId: projectBudgetFieldId,
  });

  const { hasAccess: canReadHours } = useHasAccess({
    ...commonPermissionProps,
    permissions: ['READ'],
    resourceModuleId: projectHoursFieldId,
  });

  const { hasAccess: canReadRateCard } = useHasAccess({
    ...commonPermissionProps,
    permissions: ['READ'],
    resourceModuleId: projectRateCardUpdateFieldId,
  });

  const { hasAccess: canReadCustomBillRates } = useHasAccess({
    ...commonPermissionProps,
    permissions: ['READ'],
    resourceModuleId: projectCustomBillRateFieldId,
  });

  const { hasAccess: canUpdateBudget } = useHasAccess({
    ...commonPermissionProps,
    permissions: ['WRITE'],
    resourceModuleId: projectBudgetFieldId,
  });

  const { hasAccess: canUpdateHours } = useHasAccess({
    ...commonPermissionProps,
    permissions: ['WRITE'],
    resourceModuleId: projectHoursFieldId,
  });

  const { hasAccess: canUpdateRateCard } = useHasAccess({
    ...commonPermissionProps,
    permissions: ['WRITE'],
    resourceModuleId: projectRateCardUpdateFieldId,
  });

  const { hasAccess: canUpdateCustomBillRates } = useHasAccess({
    ...commonPermissionProps,
    permissions: ['WRITE'],
    resourceModuleId: projectCustomBillRateFieldId,
  });

  const { hasAccess: canViewCostRates } = useHasAccess({
    ...commonPermissionProps,
    permissions: ['READ'],
    resourceModuleId: projectProfitabilityFieldId,
  });

  const financialsContext: ProjectFinancialsSettingsContextProps = useMemo(
    () => ({
      permissionsMap: {
        projectBudget: {
          canRead: canReadBudget,
          canWrite: canUpdateBudget,
        },
        projectHours: {
          canRead: canReadHours,
          canWrite: canUpdateHours,
        },
        projectRateCardUpdate: {
          canRead: canReadRateCard,
          canWrite: canUpdateRateCard,
        },
        projectCustomBillRate: {
          canRead: canReadCustomBillRates,
          canWrite: canUpdateCustomBillRates,
        },
        projectProfitability: {
          canRead: canViewCostRates,
        },
      },
    }),
    [
      canReadBudget,
      canReadCustomBillRates,
      canReadHours,
      canReadRateCard,
      canUpdateBudget,
      canUpdateCustomBillRates,
      canUpdateHours,
      canUpdateRateCard,
      canViewCostRates,
    ],
  );

  return financialsContext;
};
